import { useState } from 'react'
import { GalleryVerticalEnd, Maximize2, Pin } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { WindowListIcon } from '../../icons/WindowListIcon'
import { ProjectSwitcher } from '../../common/ProjectSwitcher'
import type { Project } from '../../../lib/db/models'
import './CompactListBar.scss'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'

export interface CompactListBarProps {
  /** Which compact window mode the bar fronts. Drives the stack toggle's
   *  active state + click direction (list → enter stack, stack → back to list). */
  mode?: 'list' | 'stack'
  lifeSpace: string
  currentProjectId?: string
  currentProjectName: string
  /** Switch the active project (possibly in another life space) in place. */
  onSelectProject: (project: Project) => void
  /** A life space was picked in the switcher's space menu (host syncs the
   *  global active space; the project navigation runs via onSelectProject). */
  onLifeSpaceChange?: (spaceId: string) => void
  /** Leave the compact mode → back to the full window. */
  onExit: () => void
  /** Enter stack mode (shown while in list mode). */
  onSwitchToStack?: () => void
  /** Back to list mode (shown while in stack mode). */
  onSwitchToList?: () => void
  /**
   * Whether to show the "always on top" pin toggle. Electron only — the concept
   * has no meaning on the web (no OS window to float).
   */
  showAlwaysOnTop?: boolean
  /** Current "always on top" preference (drives the pin's active colour). */
  alwaysOnTop?: boolean
  /** Toggle the compact "always on top" preference. */
  onToggleAlwaysOnTop?: () => void
}

/**
 * The compact-mode titlebar content (portaled into the titlebar slot from
 * MainLayout, which has the tab/navigation context). Project switcher centered;
 * on the right the "Stapel" toggle (enter stack / back to list), the pin and
 * the expand-to-full action (outermost). Shared by list AND stack mode — the
 * `mode` prop flips the stack toggle's state.
 */
export function CompactListBar({
  mode = 'list',
  lifeSpace,
  currentProjectId,
  currentProjectName,
  onSelectProject,
  onLifeSpaceChange,
  onExit,
  onSwitchToStack,
  onSwitchToList,
  showAlwaysOnTop = false,
  alwaysOnTop = false,
  onToggleAlwaysOnTop,
}: CompactListBarProps) {
  const { t } = useTranslation()
  // Track the dropdown so we can drop the titlebar drag region while it's open
  // (otherwise outside clicks on the drag area never reach Radix to dismiss it).
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <div className={`compact-list-bar${menuOpen ? ' compact-list-bar--menu-open' : ''}`}>
      {/* Centered relative to the WINDOW, not the (offset) titlebar slot. */}
      <div className="compact-list-bar__center">
        <ProjectSwitcher
          lifeSpace={lifeSpace}
          currentProjectId={currentProjectId}
          currentProjectName={currentProjectName}
          onSelect={onSelectProject}
          onLifeSpaceChange={onLifeSpaceChange}
          onOpenChange={setMenuOpen}
        />
      </div>
      <div className="compact-list-bar__actions">
        {/* Mode switch: in list mode the stack icon enters the deck; in stack
            mode the icon flips to the list icon and leads back — a plain
            switcher, deliberately no active/pressed styling. */}
        <WithTooltip content={mode === 'stack' ? t('project.windowMode.exitToList') : t('project.windowMode.stack')}>
          <button
            className="compact-list-bar__action"
            onClick={mode === 'stack' ? onSwitchToList : onSwitchToStack}
            aria-label={mode === 'stack' ? t('project.windowMode.exitToList') : t('project.windowMode.stack')}
          >
            {mode === 'stack' ? <WindowListIcon size={16} /> : <GalleryVerticalEnd size={16} />}
          </button>
        </WithTooltip>
        {/* Pin the compact window above other apps. In the cluster it sits
            between stack and expand (Windows); on macOS the --pin modifier
            docks it next to the red close light instead (see SCSS). Electron
            only; the active state is conveyed purely by colour (--space-700
            stroke + --space-200 fill) via the --active modifier. */}
        {showAlwaysOnTop && (
          <WithTooltip content={t('project.windowMode.alwaysOnTop')}>
            <button
              className={`compact-list-bar__action compact-list-bar__action--pin${alwaysOnTop ? ' compact-list-bar__action--active' : ''}`}
              onClick={onToggleAlwaysOnTop}
              aria-label={t('project.windowMode.alwaysOnTop')}
              aria-pressed={alwaysOnTop}
            >
              <Pin size={16} />
            </button>
          </WithTooltip>
        )}
        {/* Expand → full window, outermost right — it stands in for the hidden
            green traffic light while in list mode. On Windows this action lives
            in the native window controls instead (next to minimize), so it's
            hidden here via CSS; on macOS (no window controls) it stays in this
            cluster. */}
        <WithTooltip content={t('project.windowMode.exitToFull')}>
          <button
            className="compact-list-bar__action compact-list-bar__action--expand"
            onClick={onExit}
            aria-label={t('project.windowMode.exitToFull')}
          >
            <Maximize2 size={16} />
          </button>
        </WithTooltip>
      </div>
    </div>
  )
}
